import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, ApiError } from "../../api/client";
import type { User } from "../../types";

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiGet<User[]>("/api/admin/users"),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", password: "", role: "learner", email: "" });
  const [error, setError] = useState("");

  const createUser = useMutation({
    mutationFn: (data: typeof form) => apiPost<User>("/api/admin/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setShowForm(false);
      setForm({ username: "", name: "", password: "", role: "learner", email: "" });
      setError("");
    },
    onError: (e: ApiError) => setError(e.message),
  });

  // Role changes are staged, not applied on selection: picking a role from a
  // dropdown is too easy to do by accident for something that grants admin.
  const [pendingRole, setPendingRole] = useState<Record<string, string>>({});

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      apiPost(`/api/admin/users/${id}/role`, { role }),
    onSuccess: (_data, { id }) => {
      setPendingRole((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiPost(`/api/admin/users/${id}/active`, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  // Setting a password is for accounts that cannot self-recover — email is
  // optional at registration and never verified, so the reset link does not
  // reach everyone. Staged behind an explicit open/confirm for that reason.
  const [pwFor, setPwFor] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwNotice, setPwNotice] = useState("");

  const setPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      apiPost<{ detail: string }>(`/api/admin/users/${id}/password`, {
        new_password: password,
      }),
    onSuccess: (data) => {
      setPwNotice(`${data.detail} They have been signed out.`);
      setPwFor(null);
      setPwValue("");
    },
    onError: (e: ApiError) => setPwNotice(e.message),
  });

  // Deleting removes the account, its curriculum assignments and, for a
  // learner, their progress. Disabling was the only option before, and a
  // disabled instructor still holds their curricula — so their name stays on
  // the catalogue and there was no way to take it off.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteNotice, setDeleteNotice] = useState("");

  const deleteUser = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/users/${id}`),
    onSuccess: () => {
      setConfirmDelete(null);
      setDeleteNotice("Account deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      // Their subjects are free again, so anything listing teachers is stale.
      queryClient.invalidateQueries({ queryKey: ["admin-curricula"] });
    },
    onError: (e: ApiError) => {
      setConfirmDelete(null);
      setDeleteNotice(e.message);
    },
  });

  const [filter, setFilter] = useState("");
  const [roleFilter, setRoleFilter] =
    useState<"all" | "learner" | "instructor" | "admin">("all");

  if (isLoading) return <p className="text-muted">Loading users...</p>;

  const needle = filter.trim().toLowerCase();
  const visible = (users ?? []).filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!needle) return true;
    return [u.name, u.username, u.email, u.student_number].some((f) =>
      f?.toLowerCase().includes(needle),
    );
  });

  return (
    <div className="admin-users">
      <h1>User Management</h1>

      <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} style={{ marginBottom: 16 }}>
        {showForm ? "Cancel" : "+ Create User"}
      </button>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3>Create User</h3>
          {error && <div className="error">{error}</div>}
          <form onSubmit={(e) => { e.preventDefault(); createUser.mutate(form); }} className="create-user-form">
            <label>Username <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required /></label>
            <label>Full Name <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
            <label>Email <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label>Password <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} /></label>
            <label>Role
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="learner">Learner</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="submit" className="btn btn-primary" disabled={createUser.isPending}>
              {createUser.isPending ? "Creating..." : "Create"}
            </button>
          </form>
        </div>
      )}

      {pwNotice && <div className="notice" role="status">{pwNotice}</div>}
      {deleteNotice && <div className="notice" role="status">{deleteNotice}</div>}

      {/* Someone phoning about their account quotes a name or a student
          number, and scanning for it by eye stops working somewhere around
          thirty accounts. Client-side: the list is already loaded whole. */}
      <div className="card user-filter">
        <label>
          <span className="field-label">Find an account</span>
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Name, username, email or student number"
          />
        </label>
        <div className="user-filter-roles">
          {(["all", "learner", "instructor", "admin"] as const).map((r) => (
            <button
              key={r}
              className={`btn btn-sm ${roleFilter === r ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setRoleFilter(r)}
            >
              {r === "all" ? "Everyone" : `${r}s`}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {visible.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>
            No account matches “{filter}”.
          </p>
        ) : (
        <table>
          <thead>
            <tr><th>Username</th><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} className={!u.is_active ? "row-disabled" : ""}>
                <td>
                  <strong>{u.username}</strong>
                  {u.student_number && (
                    <div className="student-number" style={{ fontSize: "0.75rem" }}>
                      {u.student_number}
                    </div>
                  )}
                </td>
                <td>{u.name}</td>
                <td>{u.email || "—"}</td>
                <td>
                  <select
                    value={pendingRole[u.id] ?? u.role}
                    onChange={(e) =>
                      setPendingRole((p) => ({ ...p, [u.id]: e.target.value }))
                    }
                    className="inline-select"
                  >
                    <option value="learner">learner</option>
                    <option value="instructor">instructor</option>
                    <option value="admin">admin</option>
                  </select>
                  {pendingRole[u.id] && pendingRole[u.id] !== u.role && (
                    <>
                      {" "}
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={setRole.isPending}
                        onClick={() =>
                          setRole.mutate({ id: u.id, role: pendingRole[u.id] })
                        }
                      >
                        Save
                      </button>{" "}
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() =>
                          setPendingRole((p) => {
                            const next = { ...p };
                            delete next[u.id];
                            return next;
                          })
                        }
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </td>
                <td>
                  <button
                    className={`btn btn-sm ${u.is_active ? "btn-secondary" : "btn-primary"}`}
                    onClick={() => toggleActive.mutate({ id: u.id, active: !u.is_active })}
                  >
                    {u.is_active ? "Disable" : "Enable"}
                  </button>
                </td>
                <td>
                  {pwFor === u.id ? (
                    <form
                      className="inline-password-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setPassword.mutate({ id: u.id, password: pwValue });
                      }}
                    >
                      <input
                        type="text"
                        value={pwValue}
                        onChange={(e) => setPwValue(e.target.value)}
                        placeholder="New password"
                        minLength={6}
                        autoComplete="off"
                        autoFocus
                        required
                      />
                      <button
                        type="submit"
                        className="btn btn-sm btn-primary"
                        disabled={setPassword.isPending}
                      >
                        {setPassword.isPending ? "Setting…" : "Set"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setPwFor(null);
                          setPwValue("");
                        }}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setPwNotice("");
                        setPwFor(u.id);
                        setPwValue("");
                      }}
                    >
                      Set password
                    </button>
                  )}
                  {u.owned_curricula.length > 0 && (
                    <span className="text-muted"> · {u.owned_curricula.length} curricula</span>
                  )}
                  {/* Two clicks, never one: this takes the account and, for a
                      learner, every mark they have earned. */}
                  {confirmDelete === u.id ? (
                    <span className="delete-confirm">
                      Delete {u.username} for good?
                      <button
                        className="btn btn-sm btn-danger"
                        disabled={deleteUser.isPending}
                        onClick={() => deleteUser.mutate(u.id)}
                      >
                        {deleteUser.isPending ? "Deleting…" : "Yes, delete"}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Keep
                      </button>
                    </span>
                  ) : (
                    <>
                      {" "}
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setDeleteNotice("");
                          setConfirmDelete(u.id);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
